import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import { JSX, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import PopoverMenu from "@/components/ui/Popeover";
import { RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

export default function ViewArchivedAdTypes(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [adTypeToRestore, setAdTypeToRestore] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const navigate = useNavigate();
  const { getDeletedAdTypes, adCategoryApiData, restoreAdType } =
    useAdCategoryStore(
      useShallow((state) => ({
        getDeletedAdTypes: state.getDeletedAdTypes,
        adCategoryApiData: state.adCategoryApiData,
        restoreAdType: state.restoreAdType,
      })),
    );

  const setError = useGlobalStore((state) => state.setError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);

  const [searchParams] = useSearchParams();
  const adCategoryId = searchParams.get("id");

  const fetchData = async () => {
    if (!adCategoryId) return;

    try {
      setIsLoading(true);
      await getDeletedAdTypes(adCategoryId);
    } catch (error) {
      setError(error.message ?? "Failed to fetch archived ad types");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!adTypeToRestore) return;

    try {
      await restoreAdType(adTypeToRestore.id);
      showSuccess("Ad type restored successfully");
      await fetchData();
      setAdTypeToRestore(null);
    } catch (error) {
      setError(error.message ?? "Failed to restore ad type");
    }
  };

  const createMenuItems = (adTypeId: string, adTypeName: string) => [
    {
      label: "Restore Ad Type",
      icon: <RotateCcw className="w-4 h-4" />,
      onClick: () => setAdTypeToRestore({ id: adTypeId, name: adTypeName }),
    },
  ];

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return (
      <svg
        className={`w-4 h-4 text-pink-500 transition-transform ${
          sortOrder === "desc" ? "rotate-180" : ""
        }`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  };

  const goPrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const goNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  const adTypes = Array.isArray((adCategoryApiData as any)?.data?.data)
    ? (adCategoryApiData as any).data.data
    : [];

  const sortedAdTypes = [...adTypes].sort((a: any, b: any) => {
    let aValue: string;
    let bValue: string;

    switch (sortField) {
      case "name":
        aValue = a.name?.toLowerCase() || "";
        bValue = b.name?.toLowerCase() || "";
        break;
      case "publisher":
        aValue = a.publisher_id?.toLowerCase() || "";
        bValue = b.publisher_id?.toLowerCase() || "";
        break;
      case "media_type":
        aValue = a.media_type?.toLowerCase() || "";
        bValue = b.media_type?.toLowerCase() || "";
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedAdTypes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentAdTypes = sortedAdTypes.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  useEffect(() => {
    fetchData();
  }, []);

  if (!adCategoryId) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          Oops!!! The Ad Category ID is missing.
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return <Spinner className="flex justify-centers" />;
  }

  return (
    <Layout>
      <div>
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-2xl font-bold">Archived Ad Types</h1>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() =>
                navigate(`/ad-categories/publishers?id=${adCategoryId}`)
              }
              className="text-pink-500 hover:text-pink-700 text-2xl"
              aria-label="Back to Ad Categories Dashboard"
            >
              ←
            </button>
            <span className="text-black text-xl">Back to Publishers</span>
          </div>
        </div>

        <hr className="border-[#EA33C0] mb-2" />

        <div className="pt-2">
          <table className="w-full mt-5">
            <thead className="border-b border-gray-300 text-gray-600/50 text-sm">
              <tr>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("name")}
                  >
                    Publisher Name
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("name")}
                  >
                    Publisher Type
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("name")}
                  >
                    Ad Type Name
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("media_type")}
                  >
                    Media Type
                    <SortIcon field="media_type" />
                  </div>
                </th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Deleted At</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {currentAdTypes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-gray-500">
                    No Archived Ad Types Found
                  </td>
                </tr>
              ) : (
                currentAdTypes.map((adType: any) => (
                  <tr key={adType.id} className="border-b border-gray-200">
                    <td className="py-4">{adType.publisher_name || "-"}</td>
                    <td className="py-4">{adType.publisher_type || "-"}</td>
                    <td className="py-4 font-semibold">{adType.name}</td>
                    <td className="py-4">
                      {adType.media_type_name.toString().toUpperCase() || "-"}
                    </td>
                    <td className="py-4">Archived</td>
                    <td className="py-4">
                      {adType.deleted_at
                        ? new Date(adType.deleted_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="text-right">
                      <PopoverMenu
                        items={createMenuItems(adType.id, adType.name)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
          <Button
            type="secondary"
            onClick={goPrevious}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            type="secondary"
            onClick={goNext}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={!!adTypeToRestore}
        onOpenChange={() => setAdTypeToRestore(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Restore Ad Type
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to restore{" "}
              <span className="font-semibold">{adTypeToRestore?.name}</span>?
              This will make the ad type active again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setAdTypeToRestore(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              trailingIcon={<RotateCcw fontSize={18} />}
              onClick={handleRestore}
            >
              Yes, Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
